// Public directory URL model. These path forms are the contract the API sitemap
// (GET /public/resource-directory/sitemap.xml) publishes, so change both together.
//   /public/resources                                  country/state picker
//   /public/resources?state=LA                         region chooser (query based)
//   /public/resources/{state}                          results for the whole state
//   /public/resources/{state}/region/{regionId}        results for a region
//   /public/resources/{state}/category/{categoryId}    state results for a top-level category
//   /public/resources/{resourceId}                     resource detail (listing context in the query)
export const PUBLIC_RESOURCES_PATH = '/public/resources';
export const MAX_RESTORED_PAGES = 20;
const stateSegment = /^[a-z]{2}$/i;

// Runs before employee/authenticated workspace providers. Public browsing never
// bootstraps agency grants, notifications, chat connections or participant data.
export const isPublicResourcePath = (path: string) => /^\/public\/resources(?:\/[^/]+|\/[A-Za-z]{2}\/(?:region|category)\/[^/]+)?\/?$/.test(path);

export type PublicRoute =
  | { kind: 'root' }
  | { kind: 'resource'; resourceId: string }
  | { kind: 'state'; state: string }
  | { kind: 'region'; state: string; regionId: string }
  | { kind: 'category'; state: string; categoryId: string };

const decode = (value: string) => { try { return decodeURIComponent(value); } catch { return value; } };
/** Parses a public directory pathname; null means an unknown public path. */
export function parsePublicRoute(pathname: string): PublicRoute | null {
  if (!/^\/public\/resources(?:\/|$)/.test(pathname)) return null;
  const rest = pathname.slice(PUBLIC_RESOURCES_PATH.length).replace(/^\/+|\/+$/g, '');
  if (!rest) return { kind: 'root' };
  const parts = rest.split('/').map(decode);
  if (parts.length === 1) return stateSegment.test(parts[0]) ? { kind: 'state', state: parts[0].toUpperCase() } : { kind: 'resource', resourceId: parts[0] };
  if (parts.length === 3 && stateSegment.test(parts[0]) && parts[2]) {
    if (parts[1] === 'region') return { kind: 'region', state: parts[0].toUpperCase(), regionId: parts[2] };
    if (parts[1] === 'category') return { kind: 'category', state: parts[0].toUpperCase(), categoryId: parts[2] };
  }
  return null;
}

/** What a listing shows, whether it came from the path or (detail, chooser, legacy links) from the query. */
export interface ListingContext { state: string; region: string; category: string; subcategory: string; query: string; page: number; ready: boolean }
const contextKeys = new Set(['state', 'ready', 'region', 'category', 'subcategory', 'q', 'page']);
export const restoredPage = (value: string | null) => Math.min(MAX_RESTORED_PAGES, Math.max(1, Math.floor(Number(value)) || 1));
export function listingContext(route: PublicRoute | null, params: URLSearchParams): ListingContext {
  const shared = { subcategory: params.get('subcategory') || '', query: params.get('q') || '', page: restoredPage(params.get('page')) };
  const region = params.get('region') || ''; const category = params.get('category') || '';
  if (route?.kind === 'state') return { ...shared, state: route.state, region, category, ready: true };
  if (route?.kind === 'region') return { ...shared, state: route.state, region: route.regionId, category, ready: true };
  if (route?.kind === 'category') return { ...shared, state: route.state, region, category: route.categoryId, ready: true };
  return { ...shared, state: params.get('state') || '', region, category, ready: params.get('ready') === '1' };
}
const withQuery = (path: string, query: URLSearchParams) => { const text = query.toString(); return text ? `${path}?${text}` : path; };
const extraParams = (extras?: URLSearchParams) => {
  const kept = new URLSearchParams();
  extras?.forEach((value, key) => { if (!contextKeys.has(key)) kept.append(key, value); });
  return kept;
};
const setIf = (query: URLSearchParams, key: string, value: string) => { if (value) query.set(key, value); };

/** The listing URL: path form for results, query form for the country page and region chooser. */
export function listingUrl(context: ListingContext, extras?: URLSearchParams) {
  const query = new URLSearchParams();
  if (!context.state) return withQuery(PUBLIC_RESOURCES_PATH, extraParams(extras));
  if (!context.ready || !stateSegment.test(context.state)) {
    query.set('state', context.state); setIf(query, 'region', context.region); setIf(query, 'category', context.category);
    setIf(query, 'subcategory', context.subcategory); setIf(query, 'q', context.query);
    if (context.ready) { if (context.page > 1) query.set('page', String(context.page)); query.set('ready', '1'); }
    extraParams(extras).forEach((value, key) => query.append(key, value));
    return withQuery(PUBLIC_RESOURCES_PATH, query);
  }
  let path = `${PUBLIC_RESOURCES_PATH}/${context.state.toLowerCase()}`;
  if (context.region) { path += `/region/${encodeURIComponent(context.region)}`; setIf(query, 'category', context.category); }
  else if (context.category) path += `/category/${encodeURIComponent(context.category)}`;
  setIf(query, 'subcategory', context.subcategory); setIf(query, 'q', context.query);
  if (context.page > 1) query.set('page', String(context.page));
  extraParams(extras).forEach((value, key) => query.append(key, value));
  return withQuery(path, query);
}
/** A resource opened from a listing carries that listing in its query so closing returns to it. */
export function detailUrl(resourceId: string, context: ListingContext) {
  const query = new URLSearchParams();
  if (context.state) {
    query.set('state', context.state); setIf(query, 'region', context.region); setIf(query, 'category', context.category);
    setIf(query, 'subcategory', context.subcategory); setIf(query, 'q', context.query);
    if (context.page > 1) query.set('page', String(context.page));
    if (context.ready) query.set('ready', '1');
  }
  return withQuery(`${PUBLIC_RESOURCES_PATH}/${encodeURIComponent(resourceId)}`, query);
}
/** Legacy `?state=LA&ready=1` links, non-lowercase states and stray context params move to the path form. */
export function canonicalListingRedirect(route: PublicRoute | null, pathname: string, params: URLSearchParams) {
  if (!route || route.kind === 'resource') return null;
  const context = listingContext(route, params);
  if (route.kind === 'root' && !(context.ready && stateSegment.test(context.state))) return null;
  const target = listingUrl(context, params);
  return target.split('?')[0] !== pathname ? target : null;
}
export const statePath = (stateCode: string) => `${PUBLIC_RESOURCES_PATH}/${stateCode.toLowerCase()}`;
/** The indexable path of a listing: search text, subcategory and paging beyond page one are dropped or kept as documented. */
export function listingCanonicalPath(context: ListingContext) {
  const path = listingUrl({ ...context, query: '', subcategory: '', category: context.region ? '' : context.category, page: 1 });
  return context.page > 1 ? `${path}?page=${context.page}` : path;
}
